import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { getAdminId } from '../utils/getAdminId';
import type { ShippingLabel, LabelHistoryFilters, CreateLabelRequest, CreateLabelResponse } from '../types/shipping';

export const useShippingLabels = () => {
  const { user } = useAuth();
  const [labels, setLabels] = useState<ShippingLabel[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [filters, setFilters] = useState<LabelHistoryFilters>({
    status: 'all',
    dateRange: 'all'
  });

  const adminId = getAdminId(user);

  const loadLabels = useCallback(async (currentFilters?: LabelHistoryFilters) => {
    if (!adminId) return;

    setLoading(true);
    setError(null);

    try {
      const activeFilters = currentFilters || filters;
      let query = supabase
        .from('shipping_labels')
        .select('*, creator:users!created_by(full_name, email)', { count: 'exact' })
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false });

      if (activeFilters.status && activeFilters.status !== 'all') {
        query = query.eq('status', activeFilters.status);
      }

      if (activeFilters.dateRange) {
        const now = new Date();
        let startDate: Date | null = null;

        switch (activeFilters.dateRange) {
          case 'today':
            startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'custom':
            if (activeFilters.startDate) {
              startDate = new Date(activeFilters.startDate);
            }
            break;
        }

        if (startDate) {
          query = query.gte('created_at', startDate.toISOString());
        }

        if (activeFilters.dateRange === 'custom' && activeFilters.endDate) {
          query = query.lte('created_at', new Date(activeFilters.endDate).toISOString());
        }
      }

      if (activeFilters.search) {
        query = query.or(
          `recipient_name.ilike.%${activeFilters.search}%,reference.ilike.%${activeFilters.search}%,barcode.ilike.%${activeFilters.search}%,tracking_code.ilike.%${activeFilters.search}%`
        );
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error: queryError, count } = await query;

      if (queryError) throw queryError;

      setLabels(data || []);
      setTotal(count || 0);
    } catch (err) {
      setError('Failed to load labels');
      console.error('Error loading labels:', err);
    } finally {
      setLoading(false);
    }
  }, [adminId, filters, page, pageSize]);

  useEffect(() => {
    if (adminId) {
      loadLabels();
    }
  }, [adminId, page, loadLabels]);

  const createLabel = async (request: CreateLabelRequest): Promise<CreateLabelResponse> => {
    if (!user || !adminId) {
      return { success: false, error: 'Not authenticated' };
    }

    setCreating(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        throw new Error('No auth token');
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/postnl-create-manual-label`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            recipient: request.recipient,
            reference: request.reference,
            shopifyOrderId: request.shopifyOrderId,
            isReturn: request.isReturn === true,
            sender: request.sender,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create label');
      }

      await loadLabels();

      // A return label does not fix the delivery problem that opened the issue,
      // so only an outbound label may resolve it.
      if (adminId && request.reference && !request.isReturn) {
        (async () => { try { await supabase.rpc('resolve_order_issue_by_reference', {
          p_admin_id: adminId,
          p_order_reference: request.reference,
        }); } catch {} })();
      }

      return {
        success: true,
        label: result.label,
        barcode: result.barcode,
        trackingCode: result.trackingCode,
        trackingUrl: result.trackingUrl,
        pdfBase64: result.pdfBase64,
        shopifySync: result.shopifySync,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create label';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setCreating(false);
    }
  };

  const cancelLabel = async (labelId: string): Promise<boolean> => {
    if (!adminId) return false;

    try {
      const { error: updateError } = await supabase
        .from('shipping_labels')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', labelId)
        .eq('admin_id', adminId);

      if (updateError) throw updateError;

      await loadLabels();
      return true;
    } catch (err) {
      setError('Failed to cancel label');
      console.error('Error cancelling label:', err);
      return false;
    }
  };

  const updatePrintCount = async (labelId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data: label, error: fetchError } = await supabase
        .from('shipping_labels')
        .select('print_count')
        .eq('id', labelId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!label) return false;

      const { error: updateError } = await supabase
        .from('shipping_labels')
        .update({
          status: 'printed',
          print_count: (label.print_count || 0) + 1,
          last_printed_at: new Date().toISOString(),
          last_printed_by: user.id,
        })
        .eq('id', labelId);

      if (updateError) throw updateError;

      await loadLabels();
      return true;
    } catch (err) {
      console.error('Error updating print count:', err);
      return false;
    }
  };

  const getLabelById = useCallback(async (labelId: string): Promise<ShippingLabel | null> => {
    if (!adminId) return null;

    try {
      const { data, error: fetchError } = await supabase
        .from('shipping_labels')
        .select('*')
        .eq('id', labelId)
        .eq('admin_id', adminId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      console.error('Error fetching label:', err);
      return null;
    }
  }, [adminId]);

  const applyFilters = (newFilters: LabelHistoryFilters) => {
    setFilters(newFilters);
    setPage(1);
    loadLabels(newFilters);
  };

  return {
    labels,
    loading,
    creating,
    error,
    total,
    page,
    pageSize,
    filters,
    setPage,
    applyFilters,
    createLabel,
    cancelLabel,
    updatePrintCount,
    getLabelById,
    refreshLabels: loadLabels,
  };
};
